import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Modal for managing squads (subgroups) within a team: create/rename/delete
 * squads and assign/shuffle athletes between them. Assignment changes are
 * staged locally and saved in one bulk call.
 */
export default function SquadManager({ team, onClose, onChanged, variant = 'modal' }) {
  const { token } = useAuth();

  const [squads, setSquads] = useState([]);
  const [members, setMembers] = useState([]);
  const [staged, setStaged] = useState({}); // athleteId -> squadId|null (only changed rows)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [newSquadName, setNewSquadName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/squads?team=${encodeURIComponent(team)}`, {
        headers: getAuthHeaders(token)
      });
      const data = await res.json();
      if (res.ok) {
        setSquads(data.squads || []);
        setMembers(data.members || []);
        setStaged({});
        setSelectedIds(new Set());
      } else {
        flash(data.error || 'Failed to load squads');
      }
    } catch (err) {
      console.error('Error loading squads:', err);
      flash('Error loading squads');
    } finally {
      setLoading(false);
    }
  }, [team, token]);

  useEffect(() => {
    load();
  }, [load]);

  const effectiveSquadId = (member) =>
    Object.prototype.hasOwnProperty.call(staged, member.athleteId)
      ? staged[member.athleteId]
      : member.squadId;

  const stageAssignment = (athleteId, squadId) => {
    setStaged((prev) => {
      const member = members.find((m) => m.athleteId === athleteId);
      const next = { ...prev };
      if (member && member.squadId === squadId) {
        delete next[athleteId]; // reverted to original
      } else {
        next[athleteId] = squadId;
      }
      return next;
    });
  };

  const stagedCount = Object.keys(staged).length;

  const createSquad = async () => {
    const name = newSquadName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API_URL}/api/squads`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ team, name })
      });
      const data = await res.json();
      if (res.ok) {
        setNewSquadName('');
        setSquads((prev) => [...prev, data.squad]);
        onChanged?.();
      } else {
        flash(data.message ? `${data.error || 'Failed to create squad'}: ${data.message}` : (data.error || 'Failed to create squad'));
      }
    } catch (err) {
      console.error('Error creating squad:', err);
      flash('Error creating squad');
    }
  };

  const renameSquad = async (squadId) => {
    const name = renameValue.trim();
    if (!name) {
      setRenamingId(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/squads/${squadId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ name })
      });
      const data = await res.json();
      if (res.ok) {
        setSquads((prev) => prev.map((s) => (s.id === squadId ? { ...s, name: data.squad.name } : s)));
        onChanged?.();
      } else {
        flash(data.error || 'Failed to rename squad');
      }
    } catch (err) {
      console.error('Error renaming squad:', err);
      flash('Error renaming squad');
    } finally {
      setRenamingId(null);
    }
  };

  const deleteSquad = async (squad) => {
    const count = members.filter((m) => effectiveSquadId(m) === squad.id).length;
    if (!confirm(`Delete squad "${squad.name}"?${count > 0 ? ` Its ${count} member${count > 1 ? 's' : ''} will return to the whole team.` : ''} Plan sessions tagged to it become whole-team sessions.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/squads/${squad.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (res.ok) {
        await load();
        onChanged?.();
      } else {
        const data = await res.json();
        flash(data.error || 'Failed to delete squad');
      }
    } catch (err) {
      console.error('Error deleting squad:', err);
      flash('Error deleting squad');
    }
  };

  const moveSelectedTo = (squadId) => {
    setStaged((prev) => {
      const next = { ...prev };
      for (const athleteId of selectedIds) {
        const member = members.find((m) => m.athleteId === athleteId);
        if (member && member.squadId === squadId) delete next[athleteId];
        else next[athleteId] = squadId;
      }
      return next;
    });
    setSelectedIds(new Set());
  };

  const saveAssignments = async () => {
    if (stagedCount === 0) return;
    setSaving(true);
    try {
      const assignments = Object.entries(staged).map(([athleteId, squadId]) => ({
        athleteId: parseInt(athleteId, 10),
        squadId
      }));
      const res = await fetch(`${API_URL}/api/squads/assign`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ team, assignments })
      });
      const data = await res.json();
      if (res.ok) {
        flash(`${data.updated} athlete${data.updated === 1 ? '' : 's'} moved${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`);
        await load();
        onChanged?.();
      } else {
        flash(data.error || 'Failed to save assignments');
      }
    } catch (err) {
      console.error('Error saving assignments:', err);
      flash('Error saving assignments');
    } finally {
      setSaving(false);
    }
  };

  const toggleSelected = (athleteId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(athleteId)) next.delete(athleteId);
      else next.add(athleteId);
      return next;
    });
  };

  const squadName = (squadId) => squads.find((s) => s.id === squadId)?.name || 'Whole team';

  const memberCountFor = (squadId) => members.filter((m) => effectiveSquadId(m) === squadId).length;

  const isModal = variant === 'modal';

  // Deep link back to the full squads manager in Team Management (Workout/Plan
  // Builder only need quick edits here). Full page nav so App.jsx's on-mount
  // ?tab= / ?section= parsing picks it up.
  const openFullSquadManager = () => {
    window.location.href = `${window.location.pathname}?tab=teamManagement&section=squads`;
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold">Squads — {team}</h3>
        {isModal && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Squads are subgroups within your team (e.g. 1v, 2v, Frosh). Tag training plan
        sessions to a squad and athletes only see their own squad's sessions plus
        whole-team sessions.
        {isModal && (
          <>
            {' '}
            <button type="button" onClick={openFullSquadManager} className="text-theme-primary hover:underline font-medium">
              Open full squad manager →
            </button>
          </>
        )}
      </p>

        {message && (
          <div className={`mb-3 p-2 rounded text-sm ${/fail|error/i.test(message) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
            {message}
          </div>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : (
          <>
            {/* Squad list */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2 mb-2">
                {squads.map((squad) => (
                  <div key={squad.id} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full pl-3 pr-1 py-1">
                    {renamingId === squad.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => renameSquad(squad.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') renameSquad(squad.id);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        className="border rounded px-1 py-0.5 text-sm w-28 bg-white text-gray-900"
                      />
                    ) : (
                      <button
                        onClick={() => { setRenamingId(squad.id); setRenameValue(squad.name); }}
                        className="text-sm font-medium text-blue-900 hover:underline"
                        title="Click to rename"
                      >
                        {squad.name}
                      </button>
                    )}
                    <span className="text-xs text-blue-700 ml-1">{memberCountFor(squad.id)}</span>
                    <button
                      onClick={() => deleteSquad(squad)}
                      className="text-blue-400 hover:text-red-600 px-1"
                      title="Delete squad"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {squads.length === 0 && (
                  <span className="text-sm text-gray-400">No squads yet — create one below.</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSquadName}
                  onChange={(e) => setNewSquadName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createSquad()}
                  placeholder="New squad name (e.g. 1v)"
                  className="border p-2 rounded text-sm bg-white text-gray-900 flex-1 max-w-xs"
                />
                <button
                  onClick={createSquad}
                  disabled={!newSquadName.trim()}
                  className="btn-theme-primary px-3 py-2 rounded text-sm disabled:opacity-50"
                >
                  Add Squad
                </button>
              </div>
            </div>

            {/* Bulk move bar */}
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                <span className="text-sm text-blue-900">{selectedIds.size} selected — move to:</span>
                {squads.map((squad) => (
                  <button
                    key={squad.id}
                    onClick={() => moveSelectedTo(squad.id)}
                    className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                  >
                    {squad.name}
                  </button>
                ))}
                <button
                  onClick={() => moveSelectedTo(null)}
                  className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                >
                  Whole team
                </button>
              </div>
            )}

            {/* Roster */}
            <div className="border rounded overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Athlete</th>
                    <th className="p-2 text-left">Class</th>
                    <th className="p-2 text-left">Squad</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const currentSquadId = effectiveSquadId(member);
                    const isChanged = Object.prototype.hasOwnProperty.call(staged, member.athleteId);
                    return (
                      <tr key={member.athleteId} className={`border-t ${isChanged ? 'bg-amber-50' : ''}`}>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(member.athleteId)}
                            onChange={() => toggleSelected(member.athleteId)}
                          />
                        </td>
                        <td className="p-2">{member.name}</td>
                        <td className="p-2 text-gray-500">{member.racingClass || '—'}</td>
                        <td className="p-2">
                          <select
                            value={currentSquadId == null ? '' : String(currentSquadId)}
                            onChange={(e) => stageAssignment(member.athleteId, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                            className="border p-1 rounded bg-white text-gray-900 text-sm"
                          >
                            <option value="">Whole team</option>
                            {squads.map((squad) => (
                              <option key={squad.id} value={squad.id}>{squad.name}</option>
                            ))}
                          </select>
                          {isChanged && (
                            <span className="ml-2 text-xs text-amber-600" title={`Was: ${squadName(member.squadId)}`}>
                              unsaved
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {members.length === 0 && (
                    <tr><td colSpan="4" className="p-3 text-center text-gray-400">No athletes on this team.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2">
              {isModal && (
                <button
                  onClick={onClose}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Close
                </button>
              )}
              <button
                onClick={saveAssignments}
                disabled={saving || stagedCount === 0}
                className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
              >
                {saving ? 'Saving...' : `Save ${stagedCount > 0 ? `${stagedCount} change${stagedCount > 1 ? 's' : ''}` : 'changes'}`}
              </button>
            </div>
          </>
        )}
    </>
  );

  if (!isModal) {
    return <div>{content}</div>;
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5">
        {content}
      </div>
    </div>
  );
}
